import { logger } from '../../utils/logger';
import React, { useEffect, useState } from 'react';
import { UserCircleIcon, CalendarIcon, CheckCircleIcon, TrendingUpIcon, DollarSignIcon, PhoneIcon, MailIcon, MapPinIcon, ShieldIcon, FileTextIcon, MessageSquareIcon, PackageIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Select } from '../ui/Select';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { Patient, Appointment, ClinicalHistory, FollowUpNote, Opportunity } from '../../types';
import { formatDate, formatTime, formatCurrency, getStatusName, getStageName, getStageColor } from '../../utils/helpers';
import { supabase } from '../../utils/supabase';
export function Patient360() {
  const {
    patients,
    appointments,
    clinicalHistories,
    followUpNotes,
    opportunities,
    getInsurance,
    getDoctor,
    getService,
    getSpecialty
  } = useData();
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [productSales, setProductSales] = useState<any[]>([]);
  const [isLoadingSales, setIsLoadingSales] = useState(false);
  // Filter active patients
  const [patientStatuses, setPatientStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-patients-status');
      if (saved) setPatientStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  const activePatients = patients.filter(p => !patientStatuses[p.id]);
  const patientOptions = [{
    value: '',
    label: 'Seleccionar paciente...'
  }, ...activePatients.map(p => ({
    value: p.id,
    label: p.name
  }))];
  const selectedPatient = patients.find(p => p.id === selectedPatientId);
  // Fetch product sales when patient changes
  useEffect(() => {
    if (selectedPatientId) {
      const fetchSales = async () => {
        setIsLoadingSales(true);
        try {
          const {
            data,
            error
          } = await supabase.from('product_sales').select('*').eq('patient_id', selectedPatientId).order('date', {
            ascending: false
          });
          if (!error && data) {
            setProductSales(data);
          }
        } catch (err) {
          logger.error('Error fetching product sales:', err);
        } finally {
          setIsLoadingSales(false);
        }
      };
      fetchSales();
    } else {
      setProductSales([]);
    }
  }, [selectedPatientId]);
  // Derived data
  const patientAppointments = appointments.filter(a => a.patientId === selectedPatientId);
  const patientHistories = clinicalHistories.filter(h => h.patientId === selectedPatientId);
  // For follow up notes, we match by contactName (since notes use contactName instead of patientId)
  // This is a best-effort match based on the current data structure
  const patientNotes = followUpNotes.filter(n => n.contactName === selectedPatient?.name);
  // Opportunities use contactId, which might not map 1:1 to patientId, but we'll try to match by name or if contactId matches
  const patientOpportunities = opportunities.filter(o => {
    // If we have a direct link (assuming contactId might sometimes hold patientId in this CRM setup)
    if (o.contactId === selectedPatientId) return true;
    // Otherwise, try to match by name (since contact and patient might be separate entities with same name)
    if (selectedPatient && o.name.toLowerCase().includes(selectedPatient.name.toLowerCase())) return true;
    return false;
  });
  // KPIs
  const totalAppointments = patientAppointments.length;
  const completedAppointments = patientAppointments.filter(a => a.status === 'completada').length;
  const today = new Date().toISOString().split('T')[0];
  const upcomingAppointments = patientAppointments.filter(a => a.status === 'programada' && a.date >= today).length;
  // Calculate total spent (completed appointments + product sales)
  const totalSpentAppointments = patientAppointments.filter(a => a.status === 'completada').reduce((sum, a) => {
    // Handle multiple services
    if (a.serviceIds && a.serviceIds.length > 0) {
      return sum + a.serviceIds.reduce((sSum, sId) => {
        const svc = getService(sId);
        return sSum + (svc?.price || 0);
      }, 0);
    }
    // Single service fallback
    const svc = getService(a.serviceId);
    return sum + (svc?.price || 0);
  }, 0);
  const totalSpentProducts = productSales.reduce((sum, sale) => sum + Number(sale.total || 0), 0);
  const totalSpent = totalSpentAppointments + totalSpentProducts;
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };
  return <div className="space-y-6" data-id="element-3580">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 shadow-xl text-white" data-id="element-3581">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 19px, rgba(255,255,255,1) 19px, rgba(255,255,255,1) 20px)'
      }} data-id="element-3582" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-3583" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-3584" />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full border-[40px] border-white/10 pointer-events-none" data-id="element-3585" />
        <div className="absolute -bottom-10 right-32 w-40 h-40 rounded-full opacity-10 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)'
      }} data-id="element-3586" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-3587" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-3588">
          <div data-id="element-3589">
            <div className="flex items-center gap-3 mb-2" data-id="element-3590">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-3591">
                <UserCircleIcon className="w-6 h-6 text-white" data-id="element-3592" />
              </div>
              <h1 className="text-2xl font-bold text-white" data-id="element-3593">Cliente 360</h1>
            </div>
            <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-3594">
              Vista unificada del paciente
            </p>
          </div>
        </div>
      </div>

      {/* Selector */}
      <Card data-id="element-3595">
        <div className="max-w-md" data-id="element-3596">
          <Select label="Seleccionar Paciente" options={patientOptions} value={selectedPatientId} onChange={setSelectedPatientId} searchable placeholder="Buscar paciente..." data-id="element-3597" />
          
        </div>
      </Card>

      {!selectedPatient ? <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-3598">
          <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6" data-id="element-3599">
            <UserCircleIcon className="w-10 h-10 text-gray-400" data-id="element-3600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" data-id="element-3601">
            Selecciona un paciente
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-md" data-id="element-3602">
            Busca y selecciona un paciente en el menú desplegable para ver toda
            su información unificada en esta pantalla.
          </p>
        </div> : <div className="space-y-6" data-id="element-3603">
          {/* Patient Profile Card */}
          <Card className="border-t-4 border-t-teal-500 shadow-md overflow-hidden" data-id="element-3604">
            <div className="flex flex-col md:flex-row gap-6 items-start" data-id="element-3605">
              <div className="flex-shrink-0" data-id="element-3606">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white text-3xl font-bold shadow-lg" data-id="element-3607">
                  {getInitials(selectedPatient.name)}
                </div>
              </div>

              <div className="flex-1 min-w-0" data-id="element-3608">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4" data-id="element-3609">
                  <div data-id="element-3610">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1" data-id="element-3611">
                      {selectedPatient.name}
                    </h2>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mb-4" data-id="element-3612">
                      <span className="capitalize" data-id="element-3613">
                        {selectedPatient.gender}
                      </span>
                      <span data-id="element-3614">•</span>
                      <span data-id="element-3615">{selectedPatient.age} años</span>
                      {selectedPatient.birthDate && <>
                          <span data-id="element-3616">•</span>
                          <span data-id="element-3617">Nacimiento: {selectedPatient.birthDate}</span>
                        </>}
                      {selectedPatient.cedula && <>
                          <span data-id="element-3618">•</span>
                          <span data-id="element-3619">Cédula: {selectedPatient.cedula}</span>
                        </>}
                    </div>
                  </div>

                  {selectedPatient.insuranceId && <Badge variant="info" className="flex items-center gap-1" data-id="element-3620">
                      <ShieldIcon className="w-3 h-3" data-id="element-3621" />
                      {getInsurance(selectedPatient.insuranceId)?.name || 'Seguro'}
                    </Badge>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4" data-id="element-3622">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-3623">
                    <PhoneIcon className="w-4 h-4 text-gray-400" data-id="element-3624" />
                    <span data-id="element-3625">{selectedPatient.phone || 'Sin teléfono'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-3626">
                    <MailIcon className="w-4 h-4 text-gray-400" data-id="element-3627" />
                    <span className="truncate" data-id="element-3628">
                      {selectedPatient.email || 'Sin email'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-3629">
                    <MapPinIcon className="w-4 h-4 text-gray-400" data-id="element-3630" />
                    <span className="truncate" data-id="element-3631">
                      {selectedPatient.address || 'Sin dirección'}
                    </span>
                  </div>
                </div>

                {selectedPatient.emergencyContact?.name && <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-100 dark:border-gray-700 inline-block" data-id="element-3632">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1" data-id="element-3633">
                      Contacto de Emergencia
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3634">
                      {selectedPatient.emergencyContact.name} (
                      {selectedPatient.emergencyContact.relationship})
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3635">
                      {selectedPatient.emergencyContact.phone}
                    </p>
                  </div>}
              </div>
            </div>
          </Card>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3636">
            <Card padding="sm" className="bg-teal-50 dark:bg-teal-900/20 border-teal-100 dark:border-teal-800/50" data-id="element-3637">
            
              <div className="flex items-center gap-3" data-id="element-3638">
                <div className="p-3 bg-teal-100 dark:bg-teal-800/50 rounded-lg text-teal-600 dark:text-teal-400" data-id="element-3639">
                  <CalendarIcon className="w-6 h-6 text-white" data-id="element-3640" />
                </div>
                <div data-id="element-3641">
                  <p className="text-sm font-medium text-teal-600 dark:text-teal-400" data-id="element-3642">
                    Total Citas
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3643">
                    {totalAppointments}
                  </p>
                </div>
              </div>
            </Card>

            <Card padding="sm" className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800/50" data-id="element-3644">
            
              <div className="flex items-center gap-3" data-id="element-3645">
                <div className="p-3 bg-emerald-100 dark:bg-emerald-800/50 rounded-lg text-emerald-600 dark:text-emerald-400" data-id="element-3646">
                  <CheckCircleIcon className="w-6 h-6 text-white" data-id="element-3647" />
                </div>
                <div data-id="element-3648">
                  <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400" data-id="element-3649">
                    Completadas
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3650">
                    {completedAppointments}
                  </p>
                </div>
              </div>
            </Card>

            <Card padding="sm" className="bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/50" data-id="element-3651">
            
              <div className="flex items-center gap-3" data-id="element-3652">
                <div className="p-3 bg-amber-100 dark:bg-amber-800/50 rounded-lg text-amber-600 dark:text-amber-400" data-id="element-3653">
                  <TrendingUpIcon className="w-6 h-6 text-white" data-id="element-3654" />
                </div>
                <div data-id="element-3655">
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400" data-id="element-3656">
                    Próximas
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3657">
                    {upcomingAppointments}
                  </p>
                </div>
              </div>
            </Card>

            <Card padding="sm" className="bg-purple-50 dark:bg-purple-900/20 border-purple-100 dark:border-purple-800/50" data-id="element-3658">
            
              <div className="flex items-center gap-3" data-id="element-3659">
                <div className="p-3 bg-purple-100 dark:bg-purple-800/50 rounded-lg text-purple-600 dark:text-purple-400" data-id="element-3660">
                  <DollarSignIcon className="w-6 h-6 text-white" data-id="element-3661" />
                </div>
                <div data-id="element-3662">
                  <p className="text-sm font-medium text-purple-600 dark:text-purple-400" data-id="element-3663">
                    Total Gastado
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-3664">
                    {formatCurrency(totalSpent)}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs tabs={[{
        id: 'citas',
        label: `Citas (${patientAppointments.length})`,
        icon: <CalendarIcon className="w-4 h-4" data-id="element-3666" />,
        content: <Card padding="none" data-id="element-3667">
                    <div className="overflow-x-auto" data-id="element-3668">
                      <table className="w-full" data-id="element-3669">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-3670">
                          <tr data-id="element-3671">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3672">
                              Fecha
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3673">
                              Doctor
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3674">
                              Servicio
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3675">
                              Estado
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3676">
                          {patientAppointments.length === 0 ? <tr data-id="element-3677">
                              <td colSpan={4} className="py-8 text-center text-gray-500" data-id="element-3678">
                        
                                No hay citas registradas
                              </td>
                            </tr> : patientAppointments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(apt => {
                  const doctor = getDoctor(apt.doctorId);
                  const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId];
                  const serviceNames = serviceIds.map(id => getService(id)?.name).filter(Boolean).join(', ');
                  return <tr key={apt.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-3679">
                          
                                    <td className="py-3 px-4 text-sm" data-id="element-3680">
                                      <div className="font-medium text-gray-900 dark:text-white" data-id="element-3681">
                                        {formatDate(apt.date)}
                                      </div>
                                      <div className="text-gray-500 text-xs" data-id="element-3682">
                                        {formatTime(apt.time)}
                                      </div>
                                    </td>
                                    <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-300" data-id="element-3683">
                                      {doctor ? `Dr. ${doctor.name}` : '-'}
                                    </td>
                                    <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-300" data-id="element-3684">
                                      {serviceNames || '-'}
                                    </td>
                                    <td className="py-3 px-4" data-id="element-3685">
                                      <Badge variant={apt.status === 'completada' ? 'success' : apt.status === 'cancelada' ? 'danger' : apt.status === 'reprogramada' ? 'warning' : 'info'} data-id="element-3686">
                              
                                        {getStatusName(apt.status)}
                                      </Badge>
                                    </td>
                                  </tr>;
                })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }, {
        id: 'historial',
        label: `Historial Clínico (${patientHistories.length})`,
        icon: <FileTextIcon className="w-4 h-4" data-id="element-3687" />,
        content: <Card padding="none" data-id="element-3688">
                    <div className="overflow-x-auto" data-id="element-3689">
                      <table className="w-full" data-id="element-3690">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-3691">
                          <tr data-id="element-3692">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3693">
                              Fecha
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3694">
                              Doctor
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3695">
                              Motivo
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3696">
                              Diagnóstico
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3697">
                          {patientHistories.length === 0 ? <tr data-id="element-3698">
                              <td colSpan={4} className="py-8 text-center text-gray-500" data-id="element-3699">
                        
                                No hay historiales registrados
                              </td>
                            </tr> : patientHistories.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(h => <tr key={h.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-3700">
                      
                                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white" data-id="element-3701">
                                    {formatDate(h.date.toISOString().split('T')[0])}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-300" data-id="element-3702">
                                    {h.doctorName || '-'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 truncate max-w-[200px]" data-id="element-3703">
                                    {h.personalBackground?.consultationReason || '-'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 truncate max-w-[200px]" data-id="element-3704">
                                    {h.diagnosis || '-'}
                                  </td>
                                </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }, {
        id: 'notas',
        label: `Notas (${patientNotes.length})`,
        icon: <MessageSquareIcon className="w-4 h-4" data-id="element-3705" />,
        content: <div className="space-y-4" data-id="element-3706">
                    {patientNotes.length === 0 ? <Card data-id="element-3707">
                        <div className="py-8 text-center text-gray-500" data-id="element-3708">
                          No hay notas de seguimiento
                        </div>
                      </Card> : patientNotes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(note => <Card key={note.id} className="bg-gray-50 dark:bg-gray-800/30" data-id="element-3709">
                
                            <div className="flex justify-between items-start mb-2" data-id="element-3710">
                              <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-3711">
                                {note.subject}
                              </h3>
                              <span className="text-xs text-gray-500" data-id="element-3712">
                                {new Date(note.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap" data-id="element-3713">
                              {note.description}
                            </p>
                            <div className="mt-3 text-xs text-gray-500" data-id="element-3714">
                              Por: {note.userName}
                            </div>
                          </Card>)}
                  </div>
      }, {
        id: 'oportunidades',
        label: `Oportunidades (${patientOpportunities.length})`,
        icon: <TrendingUpIcon className="w-4 h-4" data-id="element-3715" />,
        content: <Card padding="none" data-id="element-3716">
                    <div className="overflow-x-auto" data-id="element-3717">
                      <table className="w-full" data-id="element-3718">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-3719">
                          <tr data-id="element-3720">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3721">
                              Nombre
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3722">
                              Tipo
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3723">
                              Etapa
                            </th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3724">
                              Monto
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3725">
                          {patientOpportunities.length === 0 ? <tr data-id="element-3726">
                              <td colSpan={4} className="py-8 text-center text-gray-500" data-id="element-3727">
                        
                                No hay oportunidades registradas
                              </td>
                            </tr> : patientOpportunities.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(opp => <tr key={opp.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-3728">
                      
                                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white" data-id="element-3729">
                                    {opp.name}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 capitalize" data-id="element-3730">
                                    {opp.opportunityType.replace(/_/g, ' ')}
                                  </td>
                                  <td className="py-3 px-4" data-id="element-3731">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStageColor(opp.stage)}`} data-id="element-3732">
                          
                                      {getStageName(opp.stage)}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 text-sm text-right font-medium text-gray-900 dark:text-white" data-id="element-3733">
                                    {formatCurrency(opp.amount)}
                                  </td>
                                </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }, {
        id: 'productos',
        label: `Productos (${productSales.length})`,
        icon: <PackageIcon className="w-4 h-4" data-id="element-3734" />,
        content: <Card padding="none" data-id="element-3735">
                    <div className="overflow-x-auto" data-id="element-3736">
                      <table className="w-full" data-id="element-3737">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-3738">
                          <tr data-id="element-3739">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3740">
                              Fecha
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3741">
                              Productos
                            </th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3742">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3743">
                          {isLoadingSales ? <tr data-id="element-3744">
                              <td colSpan={3} className="py-8 text-center text-gray-500" data-id="element-3745">
                        
                                Cargando ventas...
                              </td>
                            </tr> : productSales.length === 0 ? <tr data-id="element-3746">
                              <td colSpan={3} className="py-8 text-center text-gray-500" data-id="element-3747">
                        
                                No hay compras de productos
                              </td>
                            </tr> : productSales.map(sale => {
                  const items = Array.isArray(sale.items) ? sale.items : [];
                  const itemsDesc = items.map((i: any) => `${i.productName || 'Producto'} (x${i.quantity})`).join(', ');
                  return <tr key={sale.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-3748">
                          
                                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white" data-id="element-3749">
                                    {formatDate(sale.date)}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-3750">
                                    {itemsDesc || 'Venta de productos'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-right font-medium text-emerald-600 dark:text-emerald-400" data-id="element-3751">
                                    {formatCurrency(sale.total)}
                                  </td>
                                </tr>;
                })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }]} data-id="element-3665" />
        
        </div>}
    </div>;
}